import React, { useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardFooter } from "@/components/ui/Card";
import { Separator } from "@/components/ui/Separator";
import { ChevronLeftIcon, ChevronRightIcon } from "@radix-ui/react-icons";
import CustomAlertDialog from "@/components/custom-component/CustomAlertDialog";
import {
  TicketResponse,
  useGenerateUserTicketForServiceMutation,
  useGetAllDepartmentServiceByIdQuery,
} from "@/services/SelfServiceKiosk";
import { useSelector } from "react-redux";
import { RootState } from "@/app/store";
import { toast } from "@/components/ui/UseToast";
const { VITE_VIEW_S3_URL } = import.meta.env;
import { default as defaultImg } from "@/assets/default.jpg";
import { Department } from "@/app/types/department";
import { useLazyGetFormVersionQuery } from "@/services/FormBuilderApi";

import useTableData from "@/components/ui/table/DataTableLogic";
import usePagination from "@/hooks/UsePagination";
import { debounce } from "lodash";
import { ChevronDown, Info, Plus, Search, X } from "lucide-react";
import { Input } from "@/components/ui/Input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/AlertDialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/MobileToolTip";
import { Ticket } from "lucide-react";
import { Controller, useForm } from "react-hook-form";

export const getCurrentDateTime = () => {
  const now = new Date();
  const time = now.toLocaleTimeString("en-GB", { hour12: true }); // 24-hour format
  const date = now.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
  return { time, date };
};
interface Schedule {
  day: string;
  opening_time: string;
  closing_time: string;
  ticket_end_time: string;
}
export interface Service {
  id: number;
  name: string;
  is_multi_step: boolean;
  schedule: Schedule[];
  tags?: string[];
  logo?: string;
  form_slug?: string;
}

const initialValue = {
  limit: 8,
  offset: 0,
  service_name: "",
};
const OfferServiceCard: React.FC = () => {
  const [customTags, setCustomTags] = useState<string[]>([]);

  const [triggerGetFormVersion] = useLazyGetFormVersionQuery();
  const [selectedServiceId, setSelectedServiceId] = useState<number | null>(
    null
  );
  const dateInputRef = useRef<HTMLInputElement | null>(null);

  const [isETA, setIsETA] = React.useState(false);
  const [service, setService] = React.useState<Service | null>(null);

  const [searchTerm, setSearchTerm] = React.useState("");
  const User = useSelector((state: RootState) => state.auth.userInfo) ?? 0;

  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const department_id = id ? +id : undefined;
  const location = useLocation();
  const department: Department = location.state?.department; // Retrieve passed data

  const [currentPage, setCurrentPage] = useState<number>(0);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const itemsPerPage = 8; // Number of cards per page
  // Add these new state variables
  const [isCommandOpen, setIsCommandOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [manualTagValue, setManualTagValue] = useState("");

  const { searchCriteria, query, setSearchCriteria } =
    useTableData(initialValue);

  const {
    data: servicesData,
    isLoading,
    refetch,
    error,
  } = useGetAllDepartmentServiceByIdQuery({
    department_id: department?.id ?? (department_id as number),
    query: `${query}&ignore_schedule=true`,
  });

  const totalRecords = servicesData?.total_count ?? 0;
  React.useEffect(() => {
    refetch();
  }, [refetch]);

  const { handleNextPage, handlePrevPage, isLastPage } = usePagination({
    totalRecords,
    searchCriteria,
    setSearchCriteria,
  });

  // Calculate the slice of services to display based on the current page
  const startIndex = currentPage * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentServices = servicesData?.data?.slice(startIndex, endIndex) || [];
  const [ticketResponse, SetTicketResponse] = useState<TicketResponse>();
  const [generateTicket, { isLoading: isTicketGenerating, isError }] =
    useGenerateUserTicketForServiceMutation();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<{
    title: string;
    description?: string;
    date: string; // or Date if you want a Date object
    time: string;
  }>();

  const watcher = watch();
  // **Debounced Search Handler**
  const handleSearch = React.useCallback(
    debounce((term: string) => {
      setSearchCriteria((prev) => ({ ...prev, service_name: term }));
    }, 500),
    []
  );

  const onSubmit = async (data: {
    title: string;
    description?: string;
    date: string;
    time: string;
  }) => {
    // Ensure time is in HH:mm:ss format
    const formattedTime = data.time.includes(":")
      ? `${data.time}:00`
      : data.time;

    if (!selectedServiceId) {
      toast({
        variant: "destructive",
        title: "No Service Selected",
        description: "Please select a service before submitting.",
      });
      return;
    }

    try {
      if (User && User.id) {
        // Fetch selected service schedule
        const selectedService = servicesData?.data.find(
          (service) => service.id === selectedServiceId
        );
        if (!selectedService) {
          toast({
            variant: "destructive",
            title: "Service Not Found",
            description: "Could not find the selected service schedule.",
          });
          return;
        }

        // Convert selected date to a Date object
        const selectedDate = new Date(data.date);
        const selectedDay = selectedDate.toLocaleString("en-US", {
          weekday: "long",
        });

        // Find the service schedule for the selected day
        const serviceSchedule = selectedService.schedule.find(
          (s) => s.day === selectedDay
        );

        if (!serviceSchedule) {
          toast({
            variant: "destructive",
            title: "Service Unavailable",
            description: "This service is not available on the selected date.",
          });
          return;
        }

        // Convert opening, closing, and ticket end times to Date objects
        const [openHour, openMin] = serviceSchedule.opening_time
          .split(":")
          .map(Number);
        const [closeHour, closeMin] = serviceSchedule.closing_time
          .split(":")
          .map(Number);
        const [ticketEndHour, ticketEndMin] = serviceSchedule.ticket_end_time
          .split(":")
          .map(Number);

        const openingTime = new Date(selectedDate);
        openingTime.setHours(openHour, openMin, 0, 0);

        const closingTime = new Date(selectedDate);
        closingTime.setHours(closeHour, closeMin, 0, 0);

        const ticketEndTime = new Date(selectedDate);
        ticketEndTime.setHours(ticketEndHour, ticketEndMin, 0, 0);

        // Convert user input time to Date object
        const [userHour, userMin] = data.time.split(":").map(Number);
        const userSelectedTime = new Date(selectedDate);
        userSelectedTime.setHours(userHour, userMin, 0, 0);

        const today = new Date();
        const isSameDay = selectedDate.toDateString() === today.toDateString();

        // ✅ Case 1: User selects a time outside service hours
        if (
          userSelectedTime < openingTime ||
          userSelectedTime > ticketEndTime
        ) {
          toast({
            variant: "destructive",
            title: "Invalid Time",
            description: `Selected time must be within service hours: ${openHour % 12 || 12
              }:${String(openMin).padStart(2, "0")} ${openHour >= 12 ? "PM" : "AM"
              } - ${ticketEndHour % 12 || 12}:${String(ticketEndMin).padStart(
                2,
                "0"
              )} ${ticketEndHour >= 12 ? "PM" : "AM"},
          or please book an appointment for future date.`,
          });
          return;
        }

        // ✅ Case 2: Same-day ticket after ticket end time → ❌ Not allowed
        if (isSameDay && userSelectedTime > ticketEndTime) {
          toast({
            variant: "destructive",
            title: "Service Closed for the Day",
            description:
              "Service is closed for the day, please book an appointment for a later date.",
          });
          return;
        }

        // ✅ Case 3: Future booking outside service schedule → ❌ Not allowed
        if (!isSameDay && !serviceSchedule) {
          toast({
            variant: "destructive",
            title: "Service Unavailable",
            description:
              "Service is not available on the selected date. Please choose another date.",
          });
          return;
        }

        // ✅ Case 4: Future booking within service hours → ✅ Allowed
        // ✅ Case 5: Same-day ticket before ticket end time → ✅ Allowed

        // Prepare payload for API submission
        const payload = {
          title: data.title,
          description: data.description || "",
          date: data.date,
          time: `${formattedTime}`,
          service_id: selectedServiceId,
          user_id: Number(User.id),
          eta: 40,
        };

        // Call API to generate ticket
        await generateTicket({
          ...payload,
        })
          .unwrap()
          .then(async (res) => {
            if (res?.detail === "You already have a pending ticket for today") {
              setIsModalOpen(false);
              toast({
                variant: "destructive",
                title: "Ticket Creation Failed",
                description: "You already have a pending ticket for today.",
              });
              return;
            }

            reset({
              title: "",
              description: "", // Optional field
              date: "",
              time: "",
            });
            setSearchValue("");
            setManualTagValue("");
            setValue("title", "");
            setValue("description", "");
            setIsETA(false);
            SetTicketResponse(res);
            setIsModalOpen(true);
            setCustomTags([]);
            // Format closing time properly
            const [resHour, resMin] = res?.closing_time.split(":").map(Number);
            const closingTimeFormatted = `${resHour % 12 || 12}:${String(
              resMin
            ).padStart(2, "0")} ${resHour >= 12 ? "PM" : "AM"}`;
          })
          .catch((err) => {
            setIsModalOpen(false);
            toast({
              variant: "destructive",
              title: "Failed to create Ticket",
              description:
                err && "data" in err && typeof err.data === "object"
                  ? (err.data as any).detail ||
                  (err.data as any).message ||
                  "Something went wrong"
                  : "Something went wrong",
            });
          });
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error Generating Ticket",
        description:
          error?.data?.detail || error?.data?.message || "Something went wrong",
      });
    }
  };

  // **Update Search on Input Change**
  React.useEffect(() => {
    handleSearch(searchTerm);
  }, [searchTerm]);

  const handleTagSelect = (value: string) => {
    setValue("title", value);
    setSearchValue(value);
    setIsCommandOpen(false);
  };

  // Add this function to handle manual tag input
  const handleManualTagSubmit = () => {
    if (manualTagValue.trim()) {
      const trimmedValue = manualTagValue.trim();
      setValue("title", manualTagValue.trim());
      setSearchValue(manualTagValue.trim());
      // Add to custom tags if not already present
      if (
        !customTags.includes(trimmedValue) &&
        !service?.tags?.includes(trimmedValue)
      ) {
        setCustomTags((prev) => [...prev, trimmedValue]);
      }
      setManualTagValue("");
      setIsCommandOpen(false);
    }
  };

  const renderSkeleton = () => {
    return Array.from({ length: itemsPerPage }).map((_, index) => (
      <Card
        key={Number(index)}
        className="animate-pulse flex flex-col items-center justify-center w-full py-6 bg-[#EDEDED]"
      >
        <CardContent className="flex flex-col items-center justify-center text-center">
          <div className="w-[5rem] h-[5rem] bg-gray-300 rounded-full" />
        </CardContent>
        <CardFooter className="flex items-center justify-center">
          <div className="w-[8rem] h-[1rem] bg-gray-300 rounded-md" />
        </CardFooter>
      </Card>
    ));
  };

  const renderBody = () => {
    const closingTimeParts = ticketResponse?.closing_time
      ?.split(":")
      .map(Number) || [0, 0]; // Extract hour and minute, default to [0, 0]
    const [closingHour, closingMinute] = closingTimeParts; // Extract hour and minute

    const closingTimeAdjusted = closingHour % 12 || 12; // Convert to 12-hour format, handling 12 AM/PM case
    const period = closingHour >= 12 ? "PM" : "AM"; // Determine AM/PM

    const formattedClosingTime = `${closingTimeAdjusted}:${String(
      closingMinute
    ).padStart(2, "0")} ${period}`;

    return (
      <div className="text-center bg-white rounded-lg h-[14rem] w-full max-w-xs relative flex flex-col justify-start p-2 items-center overflow-hidden sm:max-h-[14rem] sm:overflow-y-auto">
        {/* Service Name */}
        <h2 className="text-[1.2rem] capitalize text-gray-600 font-semibold mt-0 break-words">
          {ticketResponse?.service_name}
        </h2>

        <p className="text-black text-[1.2rem] font-bold break-words">
          {ticketResponse?.assigned_number}
        </p>
        {/* Service Code */}
        <p className="text-black text-[1.8rem] font-bold break-words">
          {ticketResponse?.ticket_number}
        </p>

        {/* Information */}
        <div className="flex items-center justify-center gap-2 text-gray-600 text-nowrap">
          Counter No <br />
          <span className="text-black text-[1rem] font-bold">
            {ticketResponse?.counter_id}
          </span>
        </div>

        {/* Closing Time (positioned at the bottom center) */}
        <div className="absolute bottom-[14px] left-1/2 transform -translate-x-1/2 w-full">
          <p className="text-[0.8rem] text-gray-600 font-normal">
            Service Closing Time
          </p>
          <p className="text-[1.1rem] text-black font-semibold break-words">
            {formattedClosingTime ?? ticketResponse?.closing_time}
          </p>
        </div>
      </div>
    );
  };

  const renderNoServiceMessage = () => (
    <div className="text-center py-6 flex justify-center items-center w-full">
      <h2 className="text-gray-600 text-[1.2rem] font-semibold">
        {isLoading
          ? "Loading services..."
          : searchTerm
            ? "No Service matches your search."
            : "No Service available."}
      </h2>
      {isLoading && (
        <div className="ml-2 animate-spin">
          <svg
            className="w-5 h-5 text-primary"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
        </div>
      )}
    </div>
  );

  const getForm = async (slug: string | undefined | null, serviceId: number) => {
    if (slug !== null && slug !== undefined) {
      try {
        await triggerGetFormVersion(slug).unwrap();
        window.location.href = `/form/${slug}?service=${serviceId}`;
      } catch (error) {
        setIsETA(true); // Open the ETA modal
      }
    } else {
      setIsETA(true); // Open the ETA modal
    }
  };

  return (
    <main className="w-full flex flex-col items-center justify-center p-0 md:p-1 bg-outletcolor">
      <div className="relative w-full px-2">
        <Input
          type="text"
          placeholder="Search"
          className="mb-4  pl-10 w-[100%] md:w-[30%] "
          value={searchTerm}
          Icon={
            <Search className="absolute left-4  -top-[0.4]  z-20 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
          }
          onChange={(e) => setSearchTerm(e.target.value.trim())}
        />
      </div>

      <div className="md:m-3 w-[100%]">
        {/* Header */}

        <div className="flex pb-3 md:py-0 w-full items-center justify-between md:px-4 relative">
          <Button
            onClick={() => {
              navigate("/user/home");
            }}
            variant={"ghost"}
            className="w-16 absolute left-0"
          >
            <ChevronLeftIcon className="h-6 w-6" />
          </Button>
          <h4 className="capitalize md:my-0 text-[1.5rem] md:text-[2rem] text-primary w-full text-center">
            {department?.name}
          </h4>
        </div>
        <span className="pl-4 ">Please Select :</span>
        {/* Services Section */}
        <div className="overflow-auto custom-scrollbar h-[45vh] md:h-[65vh] p-2">
          <div className="flex justify-start items-start h-[45vh] md:h-[65vh] p-2">
            {error || currentServices.length === 0 ? (
              renderNoServiceMessage()
            ) : (
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-1 md:grid-cols-3 w-full">
                {isLoading
                  ? renderSkeleton()
                  : currentServices.map((item: Service) => (
                    <Card
                      key={item.id}
                      onClick={() => {
                        setSelectedServiceId(item.id); // Set the selected service ID
                        setService(item);
                        getForm(item.form_slug, item.id);
                      }}
                      className="group relative flex transition-all hover:shadow-lg flex-col items-center justify-center w-full  bg-secondary hover:cursor-pointer "
                    >
                      <ServiceCard item={item} />
                    </Card>
                  ))}
              </div>
            )}
          </div>
        </div>
        {/* Pagination Controls */}
        <div className="flex items-center justify-end gap-4 my-4 w-full">
          {searchCriteria.offset !== 0 && (
            <Button
              variant="solid"
              size="icon"
              onClick={handlePrevPage}
              disabled={searchCriteria.offset == 0}
              className="text-[2.1rem] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeftIcon className="h-7 w-7" />
            </Button>
          )}
          <Button
            variant="solid"
            size="icon"
            onClick={handleNextPage}
            disabled={isLastPage}
            className="text-[2.1rem] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRightIcon className="h-7 w-7" />
          </Button>
        </div>
        <Separator
          orientation="horizontal"
          className="h-[.1rem] w-[100%] bg-gray-300"
        />
        {/* Footer */}
        <div className="flex items-center justify-between my-4">
          <div>
            {/* <p className="text-[#767676] font-normal text-[0.7rem] md:text-[0.9rem]">{`${time} of ${date}`}</p> */}
          </div>
          <div className="bg-gray-300 text-gray-500 p-2 rounded-full">
            <p className="text-[.7rem] text-center md:text-[0.9rem]">
              Information & Contact
            </p>
          </div>
        </div>
      </div>

      {/* Custom Alert Dialog */}
      <CustomAlertDialog
        body={renderBody}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        isBodyAllow={true}
        title="Service Details"
        description="More details about the selected service"
        onConfirm={() => { }}
        className="w-[20rem] bg-slate-600"
      />
      {isETA && (
        <AlertDialog open={isETA} onOpenChange={() => setIsETA(false)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogDescription>
                <div className="flex flex-row items-center justify-start gap-4">
                  <Ticket className="size-14" />
                  <AlertDialogTitle className="text-sm md:text-lg font-medium">
                    Please Provide the following detail for appointment
                  </AlertDialogTitle>
                </div>
                <form
                  onSubmit={handleSubmit(onSubmit)}
                  className="mt-4 space-y-2 w-full"
                >
                  <div className="space-y-2 w-full">
                    <Label htmlFor="title" className="block text-left">
                      Title
                    </Label>
                    <div className="relative">
                      <Controller
                        name="title"
                        rules={{
                          required: "Title is required",
                          maxLength: {
                            value: 50,
                            message: "Title cannot exceed 50 characters",
                          },
                        }}
                        control={control}
                        render={({ field }) => (
                          <>
                            <Input
                              {...field}
                              value={searchValue || field.value}
                              onChange={(e) => {
                                field.onChange(e);
                                setSearchValue(e.target.value);
                              }}
                              onClick={() => setIsCommandOpen(true)}
                              placeholder="Search or add a title"
                              className="w-full border rounded p-2 pr-16"
                            />
                          </>
                        )}
                      />
                      <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex">
                        {(searchValue || watcher.title) && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setValue("title", "");
                              setSearchValue("");
                            }}
                            className="mr-1 p-1 h-auto"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="p-1 h-auto"
                          onClick={() => setIsCommandOpen(true)}
                        >
                          <ChevronDown className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    {errors.title && (
                      <p className="text-red-500 text-xs text-left">
                        {errors.title.message}
                      </p>
                    )}
                  </div>

                  {/* Command Dialog for tag selection */}
                  <CommandDialog
                    open={isCommandOpen}
                    onOpenChange={setIsCommandOpen}
                  >
                    <CommandInput
                      value={manualTagValue}
                      onValueChange={setManualTagValue}
                      placeholder="Search Title or add new..."
                    />
                    <CommandList>
                      <CommandEmpty>
                        {manualTagValue ? (
                          <div
                            onClick={() => {
                              handleTagSelect(manualTagValue);
                              setManualTagValue("");
                              handleManualTagSubmit();
                              setSearchValue("");
                            }}
                            className="px-2 py-3 flex justify-between items-center hover:bg-slate-100 cursor-pointer rounded bg-secondary"
                          >
                            <span className="flex flex-row break-all items-center ">
                              <Plus className="h-5 w-5 text-lg font-bold " />
                              Add "{manualTagValue}"
                            </span>
                          </div>
                        ) : (
                          <div className="px-2 py-3">
                            No title found. Type to create a custom title.
                          </div>
                        )}
                      </CommandEmpty>
                      <CommandGroup heading="Available Titles">
                        {[...(service?.tags || []), ...customTags]
                          .filter((tag) =>
                            tag
                              .toLowerCase()
                              .includes(manualTagValue.toLowerCase())
                          )
                          .map((tag) => (
                            <CommandItem
                              key={tag}
                              value={tag}
                              onSelect={() => handleTagSelect(tag)}
                              className="capitalize"
                            >
                              <div className="w-full whitespace-normal break-words py-1">
                                {tag}
                              </div>
                            </CommandItem>
                          ))}
                      </CommandGroup>
                    </CommandList>
                  </CommandDialog>

                  {/* Description Input */}
                  <div className="space-y-2 w-full">
                    <label
                      htmlFor="description"
                      className="block text-sm font-semibold text-start"
                    >
                      Description
                    </label>
                    <Controller
                      control={control}
                      name="description"
                      rules={{
                        maxLength: {
                          value: 350,
                          message: "description cannot exceed 350 characters",
                        },
                      }}
                      render={({ field }) => (
                        <textarea
                          {...field}
                          rows={4}
                          placeholder="Enter description"
                          className="w-full border rounded p-2"
                        />
                      )}
                    />
                    {errors.description && (
                      <p className="text-red-500 text-xs text-start">
                        {errors.description.message?.toString()}
                      </p>
                    )}
                  </div>

                  <div
                    className="space-y-2 cursor-pointer"
                    onClick={() => dateInputRef.current?.showPicker()}
                  >
                    <label
                      htmlFor="date"
                      className="block text-sm font-semibold text-start"
                    >
                      Date
                    </label>
                    <Controller
                      control={control}
                      name="date"
                      rules={{ required: "Date is required" }}
                      render={({ field }) => (
                        <TicketAvailabilityCalendar
                          service={service}
                          {...field}
                        />
                      )}
                    />
                    {errors.date && (
                      <p className="text-red-500 text-xs text-start">
                        {errors.date.message?.toString()}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2 w-full">
                    <label
                      htmlFor="date"
                      className="block text-sm font-semibold text-start"
                    >
                      Time
                    </label>
                    <Controller
                      control={control}
                      name="time"
                      rules={{ required: "Time is required" }}
                      render={({ field }) => (
                        <Input
                          {...field}
                          type="time"
                          id="time"
                          aria-label="Choose time"
                          className="w-full"
                        />
                      )}
                    />
                    {errors.time && (
                      <p className="text-red-500 text-xs text-start">
                        {" "}
                        {errors.time.message?.toString()}
                      </p>
                    )}
                  </div>

                  {/* Buttons */}
                  <div className="flex justify-end items-center gap-3 mt-4">
                    <AlertDialogCancel
                      onClick={() => {
                        setSelectedServiceId(null);
                        setIsETA(false);
                        reset({
                          title: "",
                          description: "", // Optional field
                          date: "",
                          time: "",
                        });
                        setSearchValue("");
                        setManualTagValue("");
                        setValue("title", "");
                        setValue("description", "");
                        setCustomTags([]);
                      }}
                      disabled={isTicketGenerating}
                      className="w-[100px] border border-primary font-semibold"
                    >
                      <i className="fa fa-xmark text-base px-1"></i> No
                    </AlertDialogCancel>
                    <Button
                      type="submit"
                      disabled={isTicketGenerating}
                      className="w-[140px] bg-primary text-black font-semibold"
                    >
                      <i className="fa-regular fa-floppy-disk text-base px-1"></i>{" "}
                      {isTicketGenerating ? "Generating..." : "Yes"}
                    </Button>
                  </div>
                </form>
              </AlertDialogDescription>
            </AlertDialogHeader>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </main>
  );
};

export default OfferServiceCard;

import { Control, FieldErrors } from "react-hook-form";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectGroup,
  SelectItem,
} from "@/components/ui/Select";
import { Clock } from "lucide-react";
import { Label } from "../../ui/Label";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../ui/Command";
import { days } from "@/constants/common";
import TicketAvailabilityCalendar from "./components/TicketAvailabilityCalendar";
import { useGetFormVersionQuery } from "@/services/FormBuilderApi";

interface TimeSelectorProps {
  control: Control<any>;
  errors: FieldErrors;
  service: Service;
}

// Function to generate 30-minute interval time slots with AM/PM format
const generateTimeSlots = (
  openingTime: string,
  ticketEndTime: string
): string[] => {
  const slots: string[] = [];
  let [hours, minutes] = openingTime.split(":").map(Number);
  const [endHours, endMinutes] = ticketEndTime.split(":").map(Number);

  while (hours < endHours || (hours === endHours && minutes <= endMinutes)) {
    const isPM = hours >= 12;
    const displayHours = hours % 12 || 12; // Convert 24-hour to 12-hour format
    const formattedTime = `${String(displayHours).padStart(2, "0")}:${String(
      minutes
    ).padStart(2, "0")} ${isPM ? "PM" : "AM"}`;
    slots.push(formattedTime);

    minutes += 30;
    if (minutes >= 60) {
      minutes = 0;
      hours += 1;
    }
  }

  return slots;
};

// Function to get today's day name
const getTodayDayName = (): string => {
  return days[new Date().getDay()];
};

const TimeSelector: React.FC<TimeSelectorProps> = ({
  control,
  errors,
  service,
}) => {
  const today = getTodayDayName();
  const schedule = service.schedule.find((s) => s.day === today);

  if (!schedule) {
    return (
      <p className="text-red-500 text-xs text-start">
        No available slots for today.
      </p>
    );
  }

  const timeSlots = generateTimeSlots(
    schedule.opening_time,
    schedule.ticket_end_time
  );

  return (
    <Controller
      control={control}
      name="time"
      rules={{ required: "Time is required" }}
      render={({ field }) => (
        <Select onValueChange={field.onChange} value={field.value}>
          <SelectTrigger className="relative w-full p-2 border bg-white border-gray-300 rounded-md shadow-sm text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex items-center">
            <Input
              type="text"
              value={field.value}
              placeholder="Choose time"
              readOnly
              className="w-full bg-transparent border-none focus:ring-0 cursor-pointer"
            />
            <Clock className="absolute right-2 h-4 w-4 text-gray-500 pointer-events-none" />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              {timeSlots.map((time) => (
                <SelectItem key={time} value={time}>
                  {time} {/* Displays time with AM/PM */}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      )}
    />
  );
};

// Function to convert 24-hour time to 12-hour AM/PM format
const formatTimeToAMPM = (time: string) => {
  let [hours, minutes] = time.split(":").map(Number);
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12; // Convert 0 to 12 for AM/PM
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
    2,
    "0"
  )} ${ampm}`;
};

const getTodaySchedule = (schedule: Schedule[]) => {
  const days = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  const today = days[new Date().getDay()];

  // Find today's schedule
  const todaySchedule = schedule.find((s) => s.day === today);
  if (!todaySchedule) return null;

  // Get the current time
  const currentTime = new Date();
  const [currentHours, currentMinutes] = [
    currentTime.getHours(),
    currentTime.getMinutes(),
  ];
  const currentTimeInMinutes = currentHours * 60 + currentMinutes;

  // Convert opening and closing times to minutes for easier comparison
  const [openingHours, openingMinutes] = todaySchedule.opening_time
    .split(":")
    .map(Number);
  const openingTimeInMinutes = openingHours * 60 + openingMinutes;

  const [ticketEndHours, ticketEndMinutes] = todaySchedule.ticket_end_time
    .split(":")
    .map(Number);
  const ticketEndTimeInMinutes = ticketEndHours * 60 + ticketEndMinutes;

  // If current time is outside the opening and ticket end time, return null
  if (
    currentTimeInMinutes < openingTimeInMinutes ||
    currentTimeInMinutes > ticketEndTimeInMinutes
  ) {
    return null;
  }

  return todaySchedule;
};

export const ServiceCard = ({ item }: { item: Service }) => {
  const todaySchedule = getTodaySchedule(item.schedule);
  const [isScheduleDialogOpen, setIsScheduleDialogOpen] = useState(false);

  // Function to handle info button click
  const handleInfoClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsScheduleDialogOpen(true);
  };

  const handleDialogClose = () => {
    setIsScheduleDialogOpen(false);
    // Return false to prevent propagation to parent elements
    return false;
  };

  return (
    <CardContent className="flex flex-col items-center justify-center text-center w-full px-0 pt-0 h-fit">
      {/* Info button positioned at the absolute left edge */}
      <div className="w-full relative pb-6">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="absolute left-0 top-0 p-2"
                onClick={handleInfoClick}
              >
                <Info className="h-5 w-5 text-gray-500 hover:text-primary" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>View schedule</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* Fixed-height content container */}
      <div className="flex flex-col items-center justify-center h-full">
        {/* Fixed height for image container */}
        <div className="w-full h-[120px] flex items-center justify-center mb-4">
          <img
            src={item.logo ? `${VITE_VIEW_S3_URL + item.logo}` : defaultImg}
            alt="Company logo"
            className="max-w-[100px] max-h-[100px] md:max-w-[150px] md:max-h-[150px] object-contain"
          />
        </div>

        {/* Fixed height for text content */}
        <div className="flex flex-col items-center justify-center ">
          <h3 className="capitalize text-xs md:text-base font-semibold text-black mb-2 line-clamp-2  flex items-center">
            {item.name}
          </h3>

          <div className="text-xs text-gray-500">
            {todaySchedule ? (
              <div className="flex flex-col items-center">
                <span className="font-semibold">Opening Hours:</span>
                <span>
                  {formatTimeToAMPM(todaySchedule.opening_time)} -{" "}
                  {formatTimeToAMPM(todaySchedule.ticket_end_time)}
                </span>
              </div>
            ) : (
              <p className="text-red-500">Closed today</p>
            )}
          </div>
        </div>
      </div>

      {isScheduleDialogOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center"
          onClick={(e) => {
            e.stopPropagation();
            handleDialogClose();
          }}
        >
          <div
            className="bg-white rounded-lg p-4 max-w-[90vw] md:max-w-[400px]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Service Schedule</h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDialogClose();
                }}
              >
                <X className="h-5 w-5" />
              </Button>
            </div>

            <div className="space-y-2 py-4 px-4">
              {[...item.schedule]
                .sort((a, b) => {
                  // Define the correct order of days
                  const daysOrder = [
                    "Monday",
                    "Tuesday",
                    "Wednesday",
                    "Thursday",
                    "Friday",
                    "Saturday",
                    "Sunday",
                  ];
                  return daysOrder.indexOf(a.day) - daysOrder.indexOf(b.day);
                })
                .map((sch) => (
                  <div
                    key={sch.day}
                    className="flex justify-between items-center w-full border-b pb-2 gap-3"
                  >
                    <span className="font-medium">{sch.day}</span>
                    <span>
                      {formatTimeToAMPM(sch.opening_time)} -{" "}
                      {formatTimeToAMPM(sch.ticket_end_time)}
                    </span>
                  </div>
                ))}
            </div>

            <div className="mt-4">
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDialogClose();
                }}
                className="w-full py-3 text-base"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </CardContent>
  );
};
